import { useHttpQuery } from '@/resources/http/useHttpQuery';
import { assertPresent, groupBy, maxBy } from '@luminovo/commons';
import { QuoteRequestConfigurationFields } from '@luminovo/sourcing-core';
import { UseQueryResult } from '@tanstack/react-query';

export type RecentQuoteRequestConfiguration = {
    lastUsed: string;
    count: number;
    configuration: QuoteRequestConfigurationFields;
};

export function useRecentQuoteRequestConfigurations(): UseQueryResult<RecentQuoteRequestConfiguration[]> {
    return useHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: {},
        },
        {
            select: (res) => {
                const allConfigs = res.items.map((item) => {
                    return {
                        date: item.created_at,
                        configuration: new QuoteRequestConfigurationFields({
                            required: item.configuration?.required_fields,
                            hidden: item.configuration?.hidden_fields,
                        }),
                        count: 1,
                    };
                });

                const grouped = groupBy(allConfigs, (item) => {
                    const requiredFields = Array.from(item.configuration?.required ?? []).sort();
                    const hiddenFields = Array.from(item.configuration?.hidden ?? []).sort();
                    return JSON.stringify({ requiredFields, hiddenFields });
                });

                return Object.values(grouped)
                    .map((configGroup): RecentQuoteRequestConfiguration => {
                        return {
                            lastUsed: assertPresent(maxBy(configGroup, (item) => item.date)?.date),
                            count: configGroup.reduce((acc, curr) => acc + curr.count, 0),
                            configuration: configGroup[0].configuration,
                        };
                    })
                    .sort((a, b) => b.lastUsed.localeCompare(a.lastUsed))
                    .slice(0, 5);
            },
        },
    );
}
